/**
 * i18n Setup
 * Imported once in main.tsx before the app renders. Initial language comes
 * from locale-store's persisted value (localStorage, hydrated synchronously),
 * and stays in sync with it thereafter via subscribe().
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en.json";
import sv from "./sv.json";
import { useLocaleStore } from "../stores/locale-store";

/**
 * The texts of the settings pages live in one file per page and language (admin/<page>.en.json and
 * admin/<page>.sv.json) and are put under `ap.<page>`, so a page is translated without touching the
 * shared files, and a test checks that the two languages have the same keys.
 */
function adminTexts(files: Record<string, unknown>, lang: "en" | "sv"): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [path, content] of Object.entries(files)) {
    const name = path.replace(/^.*\//, "").replace(`.${lang}.json`, "");
    out[name] = content;
  }
  return out;
}
const adminEn = adminTexts(import.meta.glob("./admin/*.en.json", { eager: true, import: "default" }), "en");
const adminSv = adminTexts(import.meta.glob("./admin/*.sv.json", { eager: true, import: "default" }), "sv");

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: { ...en, ap: adminEn } },
    sv: { translation: { ...sv, ap: adminSv } },
  },
  lng: useLocaleStore.getState().language,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

useLocaleStore.subscribe((state) => {
  if (i18n.language !== state.language) {
    i18n.changeLanguage(state.language);
  }
});

export default i18n;
